import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Link,
  Theme,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Breadcrumbs from "../../containers/Breadcrumbs";
import ReturnToLandingPageButton from "../../containers/ReturnToLandingPageButton";
import { appHeaderTitle, toTitleCase } from "../../common/services/Utilities";
import { KeyValue } from "../../interfaces";
import { formatId, ReleaseNotes } from "../../refdata/constants";
import BackButton from "../../containers/BackButton";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import Glossary from "./Glossary";
import Disclaimers from "./Disclaimers";
import Tutorials from "./Tutorials/Tutorials";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import SODOverview from "./SOD/Overview";
import SODAmendments from "./SOD/DataAmendments";
import SODMSAInfo from "./SOD/MSAInformation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {
    "margin": 0,
    "& h3": {
      paddingBottom: 0,
    },
  },
  MuiSvgIcon: {
    color: theme.palette.primary.main,
    fontSize: 10,
  },
  IndentedList: {
    color: theme.palette.primary.main,
    marginLeft: 40,
    lineHeight: "24px",
  },
  HelpTopic: {
    marginLeft: 8,
    color: theme.palette.primary.main,
  },
  HelpTopicNormalWeight: {
    marginLeft: 8,
    color: theme.palette.primary.main,
    fontWeight: "normal",
  },
  IndentedIndented: {
    color: theme.palette.primary.main,
  },
  ReleaseNotes: {
    marginLeft: 8,
    color: theme.palette.primary.main,
    paddingBottom: 0,
    lineHeight: "32px",
  },
  clickableList: {
    "margin": "0px 0px .25rem 0px",
    "& h3": {
      cursor: "pointer",
      paddingBottom: ".5rem",
      paddingTop: ".5rem",
    },
    "&:hover": {
      background: theme.palette.secondary.light,
      cursor: "pointer",
    },
    "&.active": {
      background: theme.palette.secondary.main,
    },
    "&:focus": {
      outlineColor: theme.palette.secondary.main,
    },
  },
  clickableAccordian: {
    "&:hover": {
      background: theme.palette.secondary.main,
    },
  },
  dropdownItem: {
    paddingTop: "1rem",
    paddingLeft: "1rem",
  },
}));

const introParagraph = (
  <>
    {appHeaderTitle("help")}
    <Typography variant="body2" id="help-description">
      Use the Help Topics to locate the information you’re looking for. Provide{" "}
      <Link
        href={
          "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=Help"
        }
      >
        feedback or submit a question
      </Link>{" "}
      about this page.
    </Typography>
  </>
);

export default function Help() {
  const history = useHistory();
  const urlSearch = history.location.search ? history.location.search : "";
  const classes = useStyles();
  const [helpTopic, setHelpTopic] = React.useState("");
  const [app, setApp] = React.useState("HomePage");
  const [sodExpanded, setSodExpanded] = React.useState(false);

  React.useEffect(() => {
    if (urlSearch.includes("summary-of-deposits-")) {
      setSodExpanded(true);
    }
  }, [urlSearch]);

  const handleClick = (e: any) => {
    e.preventDefault();
    if (e.currentTarget.title === "Summary of Deposits") {
      setHelpTopic("Overview");
    } else {
      const urlTopicStr: string = e.currentTarget.title.replace("and", "&");
      setHelpTopic(urlTopicStr);
    }

    if (window.location.href.split("?helpTopic=")[1] !== e.currentTarget.id) {
      const newUrl = e.currentTarget.id.replace("4-header", "overview");
      history.push({
        pathname: `/help`,
        search: `?helpTopic=${newUrl}`,
      });
    }
  };

  React.useEffect(() => {
    if (history.action === "POP" && urlSearch.includes("API")) {
      setApp("API");
    }
  }, [history, urlSearch]);
  let selectedHelpTopic = window.location.href.split("?helpTopic=")[1];
  selectedHelpTopic =
    selectedHelpTopic &&
    selectedHelpTopic
      .replace("summary-of-deposits-", "")
      .replace("and", "&")
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("-");

  React.useEffect(() => {
    if (history.action === "POP" && selectedHelpTopic) {
      const helpTopic = selectedHelpTopic
        .replace("and", "&")
        .replace(/-/g, " ");
      setHelpTopic(helpTopic);
    }
  }, [history.action, selectedHelpTopic]);

  React.useEffect(() => {
    if (!helpTopic && selectedHelpTopic) {
      const helpTopic =
        selectedHelpTopic === "msa-information"
          ? "MSA Information"
          : toTitleCase(
              selectedHelpTopic.replace("and", "&").replace(/-/g, " "),
            );
      setHelpTopic(helpTopic);
    }
    if (helpTopic === "" && !selectedHelpTopic) {
      setHelpTopic("Tutorials");
    }
  }, [helpTopic, selectedHelpTopic]);

  const HelpTopics = [
    // { title: "Data Availability", subTitle: [] },
    { title: "Tutorials", subTitle: [] },
    { title: "Glossary & Variable Definitions", subTitle: [] },
    { title: "Release Notes", subTitle: [] },
    { title: "Disclaimers & Methodologies", subTitle: [] },
    {
      title: "Summary of Deposits",
      subTitle: ["Overview", "Data Amendments", "MSA Information"],
    },
  ];

  const selectedHelpTopicTitle =
    helpTopic === "Overview" ||
    helpTopic === "Data Amendments" ||
    helpTopic === "MSA Information"
      ? `Summary of Deposits (SOD) - ${helpTopic}`
      : helpTopic;

  const topicTitle = (
    <Typography variant="h2" id={formatId(helpTopic)}>
      {selectedHelpTopicTitle}
    </Typography>
  );

  return (
    <div>
      <title>BankFind Suite: Help</title>
      <Breadcrumbs currentPageBreadCrumbTitle="BankFind Suite: Help" />
      <CloudFoundryBanner />
      <ReturnToLandingPageButton />{" "}
      <BackButton app={app} buttonLabel="Back to Previous" variant="dark" />
      {introParagraph}
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Typography variant="h2" id="help_topics">
            Help Topics
          </Typography>
          <ul style={{ color: "#1A4480" }}>
            {HelpTopics.map((topic: KeyValue, i: number) => {
              const id = `${formatId(topic.title)}-${i}`;
              const isActive = helpTopic === topic.title;

              return topic.subTitle.length ? (
                <div key={i}>
                  <Divider
                    sx={{
                      borderBottomWidth: "1px",
                      borderColor: "#acc8df",
                      my: 2,
                    }}
                  />
                  <li key={id} className={classes.listItem}>
                    <Accordion
                      sx={{
                        boxShadow: "none",
                        borderBottom: "none",
                      }}
                      disableGutters
                      expanded={sodExpanded}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${id}-content`}
                        id={`${id}-header`}
                        sx={{ padding: 0, marginTop: -0.5 }}
                        onClick={e => {
                          handleClick(e);
                          setSodExpanded(!sodExpanded);
                        }}
                        title={topic.title}
                        className={clsx(classes.clickableAccordian)}
                      >
                        <Typography
                          variant="h3"
                          className={`${classes.HelpTopic} ${
                            isActive ? "active" : ""
                          }`}
                        >
                          {topic.title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        style={{
                          padding: 0,
                        }}
                      >
                        {topic.subTitle.map((subTitle: string, j: number) => (
                          <Box
                            key={`${id}-sub-${j}`}
                            onClick={handleClick}
                            onKeyDown={e =>
                              e.key === "Enter" ? handleClick(e) : undefined
                            }
                            id={`${formatId(topic.title)}-${formatId(
                              subTitle,
                            )}`}
                            tabIndex={0}
                            title={subTitle}
                            className={clsx(
                              classes.clickableList,
                              helpTopic === subTitle ? "active" : "",
                            )}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              height: "2rem",
                            }}
                          >
                            <Typography
                              variant="body2"
                              className={clsx(
                                classes.HelpTopic,
                                classes.dropdownItem,
                              )}
                            >
                              {subTitle}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </li>
                </div>
              ) : (
                <li
                  key={id}
                  onClick={handleClick}
                  onKeyDown={e =>
                    e.key === "Enter" ? handleClick(e) : undefined
                  }
                  id={`${formatId(topic.title)}`}
                  tabIndex={0}
                  title={topic.title}
                  className={clsx(
                    classes.clickableList,
                    isActive ? "active" : "",
                  )}
                >
                  <Typography variant="h3" className={classes.HelpTopic}>
                    {topic.title}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </Grid>
        <>
          {helpTopic === "Release Notes" && (
            <Grid xs={9} padding={2}>
              {topicTitle}
              {ReleaseNotes.map((topic: KeyValue, i: number) => (
                <div key={`${formatId(topic.heading)}-${i}`}>
                  <Typography
                    variant="h2"
                    className={classes.ReleaseNotes}
                    id={formatId(topic.heading)}
                  >
                    {topic.heading}
                  </Typography>
                  <ul className={classes.listItem}>
                    {topic.title.length
                      ? topic.title.map((element: KeyValue, i: number) => (
                          <li key={`${formatId(element.subHeading)}-${i}`}>
                            <ul style={{ padding: "0 0 1rem 0" }}>
                              <Typography
                                variant="h3"
                                className={
                                  element.subTitle.length
                                    ? classes.HelpTopic
                                    : classes.HelpTopicNormalWeight
                                }
                              >
                                {element.subHeading}
                              </Typography>

                              {element.subTitle[0]
                                ? element.subTitle.map(
                                    (subTitle: string, i: number) => (
                                      <li
                                        key={`${formatId(subTitle)}-${i}`}
                                        className={classes.IndentedList}
                                      >
                                        {subTitle}
                                        {element.additionalNotes &&
                                        element.additionalNotes.length &&
                                        element.additionalNotes.length >=
                                          i + 1 ? (
                                          <ul>
                                            {element.additionalNotes.map(
                                              (
                                                additionalNotes: string,
                                                i: number,
                                              ) => (
                                                <li
                                                  key={`${formatId(
                                                    additionalNotes,
                                                  )}-${i}`}
                                                >
                                                  {additionalNotes}
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        ) : (
                                          ""
                                        )}
                                      </li>
                                    ),
                                  )
                                : ""}
                            </ul>
                          </li>
                        ))
                      : ""}
                  </ul>
                </div>
              ))}
            </Grid>
          )}
          {helpTopic === "Glossary & Variable Definitions" && (
            <Glossary title={topicTitle} />
          )}
          {helpTopic === "Disclaimers & Methodologies" && (
            <Grid item xs={9} padding={2}>
              <Disclaimers title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "Tutorials" && (
            <Grid item xs={9} padding={2}>
              <Tutorials title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "Overview" && (
            <Grid item xs={9} padding={2}>
              <SODOverview title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "Data Amendments" && (
            <Grid item xs={9} padding={2}>
              <SODAmendments title={topicTitle} />
            </Grid>
          )}
          {helpTopic === "MSA Information" && (
            <Grid item xs={9} padding={2}>
              <SODMSAInfo title={topicTitle} />
            </Grid>
          )}
        </>
      </Grid>
    </div>
  );
}
